import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SubcontractorComplianceTab from "@/components/subcontractor/SubcontractorComplianceTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Edit, ArrowLeft, Plus, Trash2, Star, Clock, Link2, FileText } from "lucide-react";
import ReliabilityBadge from "@/components/ReliabilityBadge";

const SubcontractorDetail = ({ sub, orderTypes, onBack, onEdit }) => {
  const [ordersData, setOrdersData] = useState({ orders: [], orders_count: 0, total_contract_amount: 0 });
  const [savData, setSavData] = useState({ savs: [], count: 0, total_cost: 0 });
  const [lifeEvents, setLifeEvents] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [reliability, setReliability] = useState(null);
  const [loading, setLoading] = useState(true);

  // Life event dialog
  const [lifeDialog, setLifeDialog] = useState(false);
  const [editingLife, setEditingLife] = useState(null);
  const [lifeForm, setLifeForm] = useState({ event_date: "", event_time: "", title: "", comment: "", severity: "neutral" });

  // Assign order dialog
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({ order_id: "", contract_amount: "" });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [ordRes, savRes, lifeRes, allOrdRes, relRes] = await Promise.all([
        api.getSubcontractorOrders(sub.id),
        api.getSAVsForSubcontractor(sub.id),
        api.getSubcontractorLifeEvents(sub.id),
        api.getOrders(),
        api.getSubcontractorReliability(sub.id),
      ]);
      setOrdersData(ordRes.data);
      setSavData(savRes.data);
      setLifeEvents(lifeRes.data || []);
      setAllOrders(allOrdRes.data || []);
      setReliability(relRes.data);
    } catch (_e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [sub.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- Life event handlers ---
  const openNewLife = () => {
    setEditingLife(null);
    setLifeForm({ event_date: new Date().toISOString().slice(0, 10), event_time: "", title: "", comment: "", severity: "neutral" });
    setLifeDialog(true);
  };
  const openEditLife = (ev) => {
    setEditingLife(ev);
    setLifeForm({
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      title: ev.title || "",
      comment: ev.comment || "",
      severity: ev.severity || "neutral",
    });
    setLifeDialog(true);
  };
  const saveLife = async () => {
    if (!lifeForm.event_date || !lifeForm.comment.trim()) {
      toast.error("Date et commentaire requis");
      return;
    }
    try {
      if (editingLife) {
        await api.updateSubcontractorLifeEvent(editingLife.id, {
          event_date: lifeForm.event_date,
          event_time: lifeForm.event_time || null,
          title: lifeForm.title,
          comment: lifeForm.comment,
          severity: lifeForm.severity,
        });
        toast.success("Événement mis à jour");
      } else {
        await api.createSubcontractorLifeEvent(sub.id, {
          event_date: lifeForm.event_date,
          event_time: lifeForm.event_time || null,
          title: lifeForm.title,
          comment: lifeForm.comment,
          severity: lifeForm.severity,
        });
        toast.success("Événement ajouté");
      }
      setLifeDialog(false);
      setEditingLife(null);
      fetchAll();
    } catch (_e) {
      toast.error("Erreur de sauvegarde");
    }
  };
  const deleteLife = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await api.deleteSubcontractorLifeEvent(id);
      toast.success("Événement supprimé");
      fetchAll();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  // --- Order assignment ---
  const assignableOrders = allOrders.filter(
    (o) => !o.subcontractor_id || o.subcontractor_id === sub.id
  );
  const submitAssign = async () => {
    if (!assignForm.order_id) {
      toast.error("Sélectionnez une commande");
      return;
    }
    try {
      await api.assignOrderToSubcontractor(sub.id, {
        order_id: assignForm.order_id,
        contract_amount: parseFloat(assignForm.contract_amount) || 0,
      });
      toast.success("Commande attribuée");
      setAssignDialog(false);
      setAssignForm({ order_id: "", contract_amount: "" });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'attribution");
    }
  };
  const unassignOrder = async (orderId) => {
    if (!window.confirm("Retirer cette commande du sous-traitant ?")) return;
    try {
      await api.unassignOrderFromSubcontractor(sub.id, orderId);
      toast.success("Commande retirée");
      fetchAll();
    } catch (_e) {
      toast.error("Erreur");
    }
  };

  // --- Timeline entries ---
  const buildTimeline = () => {
    const entries = [];
    ordersData.orders.forEach((o) => {
      entries.push({
        key: `ord-${o.id}`,
        date: o.created_at,
        category: "order",
        color: "bg-blue-100 text-blue-800",
        label: `Commande • ${o.status || ""}`,
        title: `${o.reference || ""} — ${o.client_name || ""}`,
        description: `${o.order_type_name || ""}${o.subcontractor_contract_amount ? ` • Contrat ${formatCurrency(o.subcontractor_contract_amount)}` : ""}${o.amount_ht ? ` • HT ${formatCurrency(o.amount_ht)}` : ""}`,
      });
    });
    savData.savs.forEach((s) => {
      entries.push({
        key: `sav-${s.id}`,
        date: s.intervention_date,
        category: "sav",
        color: "bg-purple-100 text-purple-800",
        label: `SAV${s.status ? ` • ${s.status}` : ""}`,
        title: `SAV : ${s.client_name || ""}${s.order_reference ? ` (${s.order_reference})` : ""}`,
        description: `${s.cost ? formatCurrency(s.cost) + " — " : ""}${s.description || ""}`.trim(),
      });
    });
    lifeEvents.forEach((ev) => {
      entries.push({
        key: `life-${ev.id}`,
        date: ev.event_date,
        time: ev.event_time,
        category: "life-event",
        color: "bg-amber-100 text-amber-800",
        label: ev.title || "Événement marquant",
        title: ev.title || "Événement marquant",
        description: ev.comment,
      });
    });
    entries.sort((a, b) => {
      const ak = `${a.date || ""}T${a.time || "00:00"}`;
      const bk = `${b.date || ""}T${b.time || "00:00"}`;
      return bk.localeCompare(ak);
    });
    return entries;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" /> Retour</Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">{sub.company_name || `${sub.first_name || ""} ${sub.last_name || ""}`.trim()}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-muted-foreground">Sous-traitant</p>
              {reliability && <ReliabilityBadge reliability={reliability} size="lg" />}
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={onEdit}><Edit className="h-4 w-4 mr-2" /> Modifier</Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="sub-tab-overview">Informations</TabsTrigger>
          <TabsTrigger value="orders" data-testid="sub-tab-orders">
            Commandes {ordersData.orders_count > 0 && <Badge variant="outline" className="ml-1">{ordersData.orders_count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="sav" data-testid="sub-tab-sav">
            SAV {savData.count > 0 && <Badge variant="outline" className="ml-1">{savData.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="life-events" data-testid="sub-tab-life">
            <Star className="h-4 w-4 mr-1" /> Événements marquants
            {lifeEvents.length > 0 && <Badge variant="outline" className="ml-1">{lifeEvents.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="sub-tab-timeline">
            <Clock className="h-4 w-4 mr-1" /> Chronologie
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="sub-tab-compliance">
            <FileText className="h-4 w-4 mr-1" /> Conformité
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Informations</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div><span className="text-muted-foreground">Contact :</span> {`${sub.first_name || ""} ${sub.last_name || ""}`.trim() || "-"}</div>
                <div><span className="text-muted-foreground">Tel. fixe :</span> {sub.phone_fixed || "-"}</div>
                <div><span className="text-muted-foreground">Tel. mobile :</span> {sub.phone_mobile || "-"}</div>
                <div><span className="text-muted-foreground">Adresse :</span> {sub.address || "-"}</div>
                <div><span className="text-muted-foreground">TVA :</span> {sub.tva_number || "-"}</div>
                <div><span className="text-muted-foreground">SIRET :</span> {sub.siret || "-"}</div>
                <div className="pt-2">
                  <div className="text-muted-foreground text-xs mb-1">Types de commandes :</div>
                  <div className="flex flex-wrap gap-1">
                    {(sub.order_type_ids || []).map(id => {
                      const ot = orderTypes.find(o => o.id === id);
                      return ot ? <Badge key={id} variant="secondary">{ot.name}</Badge> : null;
                    })}
                    {(sub.order_type_ids || []).length === 0 && <span className="text-xs text-muted-foreground">-</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Statistiques</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {reliability && (
                  <div className="p-3 rounded border bg-muted/30">
                    <div className="text-xs text-muted-foreground mb-1">Score de fiabilité (12 derniers mois)</div>
                    <div className="flex items-center gap-2">
                      <div className="text-3xl font-bold">{reliability.score}<span className="text-sm text-muted-foreground">/100</span></div>
                      <ReliabilityBadge reliability={reliability} />
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground mt-2">
                      <div>Commandes : <strong className="text-foreground">{reliability.orders_12m}</strong></div>
                      <div>SAV : <strong className="text-foreground">{reliability.sav_12m}</strong></div>
                      <div>Ratio SAV : <strong className="text-foreground">{Math.round((reliability.sav_ratio || 0) * 100)}%</strong></div>
                      <div>Événements − : <strong className="text-red-700">{reliability.negative_events_12m}</strong></div>
                      <div>Événements + : <strong className="text-green-700">{reliability.positive_events_12m}</strong></div>
                    </div>
                  </div>
                )}
                <div><div className="text-sm text-muted-foreground">Commandes (total)</div><div className="text-3xl font-bold">{ordersData.orders_count}</div></div>
                <div><div className="text-sm text-muted-foreground">Total contrats</div><div className="text-3xl font-bold text-primary">{formatCurrency(ordersData.total_contract_amount)}</div></div>
                <div><div className="text-sm text-muted-foreground">SAV (total)</div><div className="text-3xl font-bold text-amber-600">{savData.count}</div></div>
                <div><div className="text-sm text-muted-foreground">Coût SAV</div><div className="text-3xl font-bold text-red-600">{formatCurrency(savData.total_cost)}</div></div>
                {sub.comments && <div className="pt-2"><div className="text-xs text-muted-foreground mb-1">Commentaires :</div><p className="text-sm whitespace-pre-wrap">{sub.comments}</p></div>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Commandes</CardTitle>
              <Button size="sm" onClick={() => setAssignDialog(true)} data-testid="assign-order-btn">
                <Link2 className="h-4 w-4 mr-1" /> Attribuer une commande
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : ordersData.orders.length === 0 ? (
                <p className="text-center text-muted-foreground py-6">Aucune commande attribuée à ce sous-traitant.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Référence</TableHead><TableHead>Client</TableHead><TableHead>Type</TableHead>
                      <TableHead>Date</TableHead><TableHead>Montant HT</TableHead><TableHead>Contrat</TableHead>
                      <TableHead className="text-right">Ratio</TableHead>
                      <TableHead>Statut</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ordersData.orders.map(o => {
                      const amt = parseFloat(o.amount_ht) || 0;
                      const contract = parseFloat(o.subcontractor_contract_amount) || 0;
                      const ratio = amt > 0 ? (contract / amt) * 100 : null;
                      const ratioClass = ratio === null
                        ? "text-muted-foreground"
                        : ratio >= 80 ? "text-red-600 font-semibold"
                        : ratio >= 60 ? "text-amber-600 font-semibold"
                        : "text-green-700 font-semibold";
                      return (
                        <TableRow key={o.id} data-testid={`sub-order-${o.id}`}>
                          <TableCell>{o.reference}</TableCell>
                          <TableCell>{o.client_name}</TableCell>
                          <TableCell>{o.order_type_name}</TableCell>
                          <TableCell>{formatDate(o.created_at)}</TableCell>
                          <TableCell>{formatCurrency(amt)}</TableCell>
                          <TableCell className="font-semibold">{formatCurrency(contract)}</TableCell>
                          <TableCell className={`text-right ${ratioClass}`} data-testid={`sub-order-ratio-${o.id}`}>
                            {ratio === null ? "—" : `${ratio.toFixed(1)}%`}
                          </TableCell>
                          <TableCell><Badge variant="outline">{o.status}</Badge></TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => unassignOrder(o.id)} data-testid={`unassign-order-${o.id}`} title="Retirer">
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(() => {
                      const totals = ordersData.orders.reduce((acc, o) => {
                        acc.amt += parseFloat(o.amount_ht) || 0;
                        acc.contract += parseFloat(o.subcontractor_contract_amount) || 0;
                        return acc;
                      }, { amt: 0, contract: 0 });
                      const avgRatio = totals.amt > 0 ? (totals.contract / totals.amt) * 100 : null;
                      const cls = avgRatio === null
                        ? "text-muted-foreground"
                        : avgRatio >= 80 ? "text-red-600"
                        : avgRatio >= 60 ? "text-amber-600"
                        : "text-green-700";
                      return (
                        <TableRow className="border-t-2 bg-muted/40 font-bold" data-testid="sub-orders-totals">
                          <TableCell colSpan={4}>Totaux</TableCell>
                          <TableCell>{formatCurrency(totals.amt)}</TableCell>
                          <TableCell>{formatCurrency(totals.contract)}</TableCell>
                          <TableCell className={`text-right ${cls}`} data-testid="sub-orders-avg-ratio">
                            {avgRatio === null ? "—" : `${avgRatio.toFixed(1)}%`}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sav" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle>SAV ({savData.count})</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : savData.savs.length === 0 ? (
                <p className="text-center text-muted-foreground py-6">Aucun SAV.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead><TableHead>Description</TableHead>
                      <TableHead>Commande</TableHead><TableHead>Date</TableHead>
                      <TableHead>Coût</TableHead><TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {savData.savs.map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.client_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{s.description}</TableCell>
                        <TableCell>{s.order_reference ? <Badge variant="outline" className="font-mono text-xs">{s.order_reference}</Badge> : "-"}</TableCell>
                        <TableCell>{formatDate(s.intervention_date)}</TableCell>
                        <TableCell className="font-mono font-bold">{formatCurrency(s.cost)}</TableCell>
                        <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                      <TableCell colSpan={4} className="font-bold text-right">Total</TableCell>
                      <TableCell className="font-mono font-bold text-primary">{formatCurrency(savData.total_cost)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="life-events" className="space-y-4 pt-4">
          <Card data-testid="sub-life-events-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Événements marquants</CardTitle>
              <Button size="sm" onClick={openNewLife} data-testid="sub-add-life-event-btn">
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {lifeEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded">
                  Aucun événement marquant enregistré.
                </p>
              ) : (
                <div className="space-y-2">
                  {lifeEvents.map((ev) => (
                    <div key={ev.id} className="flex items-start justify-between p-3 rounded border bg-card" data-testid={`sub-life-event-${ev.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <span className="font-mono font-medium">
                            {new Date(ev.event_date).toLocaleDateString("fr-FR")}
                            {ev.event_time ? ` à ${ev.event_time}` : ""}
                          </span>
                          {ev.title && <Badge variant="outline">{ev.title}</Badge>}
                          {ev.severity === "positive" && (
                            <Badge className="bg-green-100 text-green-800 text-xs">✅ Positif</Badge>
                          )}
                          {ev.severity === "negative" && (
                            <Badge className="bg-red-100 text-red-800 text-xs">⚠️ Négatif</Badge>
                          )}
                        </div>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{ev.comment}</p>
                        {ev.created_by && (
                          <p className="text-xs text-muted-foreground mt-1">Ajouté par {ev.created_by}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditLife(ev)} data-testid={`sub-edit-life-event-${ev.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLife(ev.id)} data-testid={`sub-delete-life-event-${ev.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4 pt-4">
          <Card data-testid="sub-timeline-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Chronologie du sous-traitant
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Commandes, SAV et événements marquants regroupés sur un axe temporel.
              </p>
            </CardHeader>
            <CardContent>
              {(() => {
                const entries = buildTimeline();
                if (entries.length === 0) {
                  return <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded">Aucune donnée chronologique.</p>;
                }
                const byYear = {};
                entries.forEach((ev) => {
                  const y = ev.date ? new Date(ev.date).getFullYear() : "—";
                  (byYear[y] = byYear[y] || []).push(ev);
                });
                return (
                  <div className="space-y-6" data-testid="sub-timeline-entries">
                    {Object.keys(byYear).sort((a, b) => String(b).localeCompare(String(a))).map((y) => (
                      <div key={y}>
                        <h3 className="text-sm font-heading font-bold text-muted-foreground mb-3 sticky top-0 bg-card py-1">{y}</h3>
                        <div className="relative border-l-2 border-border pl-4 space-y-3">
                          {byYear[y].map((ev) => (
                            <div key={ev.key} className="relative" data-testid={`sub-timeline-entry-${ev.category}`}>
                              <span className="absolute -left-[22px] top-1 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                              <div className="flex items-start gap-3">
                                <span className="text-xs font-mono tabular-nums text-muted-foreground min-w-[90px]">
                                  {ev.date ? new Date(ev.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                                  {ev.time ? ` · ${ev.time}` : ""}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="secondary" className={`${ev.color} text-xs`}>{ev.label}</Badge>
                                    <span className="text-sm font-medium">{ev.title}</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{ev.description}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4 pt-4">
          <SubcontractorComplianceTab sub={sub} onUpdated={() => { /* parent fetch optional */ }} />
        </TabsContent>
      </Tabs>

      {/* Assign order dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attribuer une commande à {sub.company_name || `${sub.first_name || ""} ${sub.last_name || ""}`.trim()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Commande *</Label>
              <Select value={assignForm.order_id} onValueChange={(v) => setAssignForm({ ...assignForm, order_id: v })}>
                <SelectTrigger data-testid="assign-order-select">
                  <SelectValue placeholder="Sélectionner une commande..." />
                </SelectTrigger>
                <SelectContent>
                  {assignableOrders.length === 0 && (
                    <div className="text-xs text-muted-foreground p-2">Aucune commande disponible</div>
                  )}
                  {assignableOrders.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.reference} — {o.client_name} ({formatCurrency(o.amount_ht)})
                      {o.subcontractor_id === sub.id ? " ✓ déjà attribuée" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Les commandes déjà attribuées à un autre sous-traitant sont masquées.
              </p>
            </div>
            <div>
              <Label>Montant contrat sous-traitance (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={assignForm.contract_amount}
                onChange={(e) => setAssignForm({ ...assignForm, contract_amount: e.target.value })}
                data-testid="assign-contract-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(false)}>Annuler</Button>
            <Button onClick={submitAssign} data-testid="assign-order-confirm">Attribuer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Life event dialog */}
      <Dialog open={lifeDialog} onOpenChange={setLifeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLife ? "Modifier l'événement" : "Nouvel événement marquant"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={lifeForm.event_date} onChange={(e) => setLifeForm({ ...lifeForm, event_date: e.target.value })} data-testid="sub-life-event-date" />
              </div>
              <div>
                <Label>Heure</Label>
                <Input type="time" value={lifeForm.event_time} onChange={(e) => setLifeForm({ ...lifeForm, event_time: e.target.value })} data-testid="sub-life-event-time" />
              </div>
            </div>
            <div>
              <Label>Titre (optionnel)</Label>
              <Input value={lifeForm.title} onChange={(e) => setLifeForm({ ...lifeForm, title: e.target.value })} placeholder="Ex : Retard de livraison, Incident..." data-testid="sub-life-event-title" />
            </div>
            <div>
              <Label>Impact sur la fiabilité</Label>
              <Select value={lifeForm.severity} onValueChange={(v) => setLifeForm({ ...lifeForm, severity: v })}>
                <SelectTrigger data-testid="sub-life-event-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">✅ Positif (+5 points au score)</SelectItem>
                  <SelectItem value="neutral">⚪ Neutre (aucun impact)</SelectItem>
                  <SelectItem value="negative">⚠️ Négatif (-10 points au score)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commentaire *</Label>
              <Textarea value={lifeForm.comment} onChange={(e) => setLifeForm({ ...lifeForm, comment: e.target.value })} rows={5} data-testid="sub-life-event-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLifeDialog(false)}>Annuler</Button>
            <Button onClick={saveLife} data-testid="sub-save-life-event-btn">
              {editingLife ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubcontractorDetail;
